import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/formatDate';
import { UserCheck, UserX, Plus, X, CheckCircle, AlertCircle } from 'lucide-react';

const Toast = ({ message, type = 'info', onClose }) => (
  <div className={`toast toast-${type}`}>
    {type === 'success' && <CheckCircle size={14} style={{ color: 'var(--green)', flexShrink: 0 }} />}
    {type === 'error'   && <AlertCircle size={14} style={{ color: 'var(--red)',   flexShrink: 0 }} />}
    <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-1)' }}>{message}</span>
    <button className="btn-icon" style={{ width: '24px', height: '24px', border: 'none' }} onClick={onClose}>
      <X size={12} />
    </button>
  </div>
);

export const Technicians = () => {
  const { fetchAuthorized } = useAuth();
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [toast, setToast]       = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ telegram_id: '', full_name: '', username: '' });
  const [saving, setSaving] = useState(false);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(() => {
    setLoading(true);
    fetchAuthorized('/api/dashboard/technicians')
      .then(r => r.json())
      .then(d => { if (d.success) setTechnicians(d.technicians || []); })
      .catch(() => showToast('Ошибка загрузки', 'error'))
      .finally(() => setLoading(false));
  }, [fetchAuthorized]);

  useEffect(() => { load(); }, [load]);

  const handleDeactivate = async (tech) => {
    if (!window.confirm(`Деактивировать сервисника ${tech.full_name || tech.telegram_id}?`)) return;
    try {
      const r = await fetchAuthorized(`/api/dashboard/technicians/${tech.id}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.success) { showToast('Сервисник деактивирован', 'success'); load(); }
      else showToast(d.error || 'Ошибка', 'error');
    } catch { showToast('Ошибка', 'error'); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.telegram_id.trim()) return showToast('Укажите Telegram ID', 'error');
    setSaving(true);
    try {
      const r = await fetchAuthorized('/api/dashboard/technicians', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (d.success) {
        showToast('Сервисник добавлен', 'success');
        setForm({ telegram_id: '', full_name: '', username: '' });
        setShowForm(false);
        load();
      } else showToast(d.error || 'Ошибка', 'error');
    } catch { showToast('Ошибка', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-1)' }}>
          Сервисники
        </h1>
        <button
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'var(--brand)', color: '#fff', fontSize: '13px' }}
          onClick={() => setShowForm(f => !f)}
        >
          <Plus size={14} />
          Добавить
        </button>
      </div>

      {showForm && (
        <div className="glass-card" style={{ marginBottom: '20px', padding: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-1)', marginBottom: '16px' }}>
            Новый сервисник
          </h3>
          <form onSubmit={handleAdd} style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-2)' }}>Telegram ID *</label>
              <input
                className="search-input"
                style={{ width: '150px' }}
                value={form.telegram_id}
                onChange={e => setForm(f => ({ ...f, telegram_id: e.target.value }))}
                placeholder="123456789"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-2)' }}>Полное имя</label>
              <input
                className="search-input"
                style={{ width: '200px' }}
                value={form.full_name}
                onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Иван Иванов"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-2)' }}>Username (без @)</label>
              <input
                className="search-input"
                style={{ width: '160px' }}
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                placeholder="ivan_tech"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              style={{ padding: '8px 20px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text-2)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
            >
              Отмена
            </button>
          </form>
        </div>
      )}

      <div className="glass-card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>Загрузка...</div>
        ) : technicians.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
            Нет сервисников. Добавьте первого.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Telegram ID', 'Имя', 'Username', 'Статус', 'Добавлен', ''].map((h, i) => (
                  <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: 'var(--text-3)', fontWeight: 600 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {technicians.map(tech => (
                <tr key={tech.id} style={{ borderBottom: '1px solid var(--border)', opacity: tech.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-1)', fontFamily: 'monospace' }}>
                    {tech.telegram_id}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-1)' }}>
                    {tech.full_name || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-2)' }}>
                    {tech.username ? `@${tech.username}` : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {tech.is_active ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: 'var(--green)' }}>
                        <UserCheck size={14} /> Активен
                      </span>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: 'var(--text-3)' }}>
                        <UserX size={14} /> Деактивирован
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-2)' }}>
                    {fmtDate(tech.created_at)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {tech.is_active && (
                      <button
                        className="btn-icon"
                        title="Деактивировать"
                        onClick={() => handleDeactivate(tech)}
                        style={{ color: 'var(--red)' }}
                      >
                        <UserX size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
